import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ChordDiagram } from '@/components/ChordDiagram';
import { Ornament } from '@/components/Ornament';
import { Card } from '@/components/ui';
import { CHORDS } from '@/data/chords';
import { t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import type { Chord } from '@/types/music';
import './ChordsLibraryPage.css';

type Filter = 'all' | 'beginner' | 'common' | 'barre';

const FILTERS: { id: Filter; labelKey: TranslationKey }[] = [
  { id: 'beginner', labelKey: 'chords.filterBeginner' },
  { id: 'common', labelKey: 'chords.filterCommon' },
  { id: 'barre', labelKey: 'chords.filterBarre' },
  { id: 'all', labelKey: 'chords.filterAll' },
];

export function ChordsLibraryPage() {
  const [filter, setFilter] = useState<Filter>('beginner');
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return CHORDS.filter((chord) => matchesFilter(chord, filter)).filter((chord) =>
      normalized
        ? chord.name.toLowerCase().includes(normalized) ||
          chord.nameRu.toLowerCase().includes(normalized)
        : true,
    );
  }, [filter, query]);

  return (
    <AppShell>
      {/* Top-level tabs keep the brand in the header and carry their own H1,
          the same way the Progress screen does. */}
      <h1 className="library__title">{t('chords.title')}</h1>

      <div className="library__search">
        <Search size={20} aria-hidden="true" className="library__search-icon" />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('chords.search')}
          aria-label={t('chords.search')}
        />
      </div>

      <div className="library__filters" role="group" aria-label={t('chords.title')}>
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="chip"
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <Card>
          <p className="library__empty-title">{t('chords.empty')}</p>
          <p className="muted">{t('chords.emptyHint')}</p>
        </Card>
      ) : (
        <ul className="library__grid">
          {visible.map((chord) => (
            <li key={chord.id}>
              <Link to={`/chords/${chord.id}`} className="library__card">
                <span className="library__card-name">{chord.name}</span>
                <ChordDiagram chord={chord} size={104} showFingers={false} />
              </Link>
            </li>
          ))}
        </ul>
      )}

      <Card section className="library__tip">
        <Ornament kind="guitar" position="left" size={130} />
        <div className="library__tip-text">
          <p className="library__tip-title">{t('chords.startHere')}</p>
          <p className="muted">{t('chords.startHereHint')}</p>
        </div>
      </Card>
    </AppShell>
  );
}

function matchesFilter(chord: Chord, filter: Filter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'barre':
      // The barre filter is defined by the shape, not by the category label —
      // an F is a barre chord no matter which bucket it is filed under.
      return chord.barre !== undefined;
    case 'beginner':
      return chord.category === 'beginner';
    case 'common':
      return chord.category === 'common' || chord.category === 'extended';
    default:
      return true;
  }
}
